// ---------------------------------------------------------------------------
// Project selector — dropdown that selects the current project context
// ---------------------------------------------------------------------------

import React, { useCallback, useEffect, useState } from 'react';
import type { admin } from '@noxverify/api-contracts';
import { api } from '../lib/api';
import { useProjectContext } from '../lib/project-context';

interface TenantWithProjects {
  tenant: admin.TenantSummary;
  projects: admin.ProjectSummary[];
}

export function ProjectSelector() {
  const { tenantId, projectId, setProject } = useProjectContext();
  const [tenantsWithProjects, setTenantsWithProjects] = useState<
    TenantWithProjects[]
  >([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);

  const loadData = useCallback(() => {
    setLoading(true);
    api.admin
      .listTenants()
      .then(async (res) => {
        const results: TenantWithProjects[] = [];
        for (const tenant of res.data) {
          try {
            const projectsRes = await api.admin.listProjects(tenant.id);
            results.push({ tenant, projects: projectsRes.data });
          } catch {
            results.push({ tenant, projects: [] });
          }
        }
        setTenantsWithProjects(results);
      })
      .catch(() => {
        // Silently fail — the selector will show placeholder
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const selectedLabel = React.useMemo(() => {
    if (!tenantId || !projectId) return null;
    for (const { tenant, projects } of tenantsWithProjects) {
      if (tenant.id === tenantId) {
        const project = projects.find((p) => p.id === projectId);
        if (project) {
          return `${tenant.name} / ${project.name}`;
        }
      }
    }
    return null;
  }, [tenantId, projectId, tenantsWithProjects]);

  const handleSelect = (tid: string, pid: string) => {
    setProject(tid, pid);
    setOpen(false);
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        disabled={loading}
        aria-expanded={open}
        aria-haspopup="listbox"
        className="flex items-center gap-2 px-3 py-1.5 text-sm rounded-sm border border-hairline-strong bg-white text-ink hover:border-ink focus:outline-none focus:ring-2 focus:ring-primary/25 focus:ring-offset-1 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <span className="max-w-[200px] truncate">
          {loading
            ? 'Loading...'
            : selectedLabel ?? 'Select a project...'}
        </span>
        <svg
          className={`w-4 h-4 text-ink-muted transition-transform ${open ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {open && (
        <>
          {/* Backdrop to close on outside click */}
          <div
            className="fixed inset-0 z-10"
            onClick={() => setOpen(false)}
            aria-hidden="true"
          />
          <div
            role="listbox"
            aria-label="Select project"
            className="absolute end-0 z-20 mt-1 w-72 max-h-80 overflow-auto rounded-sm bg-surface-raised shadow-md border border-hairline"
          >
            {tenantsWithProjects.length === 0 ? (
              <div className="px-4 py-3 text-sm text-ink-muted">
                No projects available
              </div>
            ) : (
              tenantsWithProjects.map(({ tenant, projects }) => (
                <div key={tenant.id}>
                  <div className="px-3 py-2 font-mono text-[0.6875rem] font-medium text-ink-muted uppercase tracking-[0.08em] bg-surface-sunken">
                    {tenant.name}
                  </div>
                  {projects.length === 0 ? (
                    <div className="px-4 py-2 text-sm text-ink-muted italic">
                      No projects
                    </div>
                  ) : (
                    projects.map((project) => (
                      <button
                        key={project.id}
                        type="button"
                        role="option"
                        aria-selected={
                          project.id === projectId &&
                          tenant.id === tenantId
                        }
                        onClick={() =>
                          handleSelect(tenant.id, project.id)
                        }
                        className={`w-full text-start px-4 py-2 text-sm hover:bg-primary-tint focus:bg-primary-tint focus:outline-none ${
                          project.id === projectId &&
                          tenant.id === tenantId
                            ? 'bg-primary-tint text-primary font-medium'
                            : 'text-ink'
                        }`}
                      >
                        {project.name}
                        <span className="ms-2 text-xs text-ink-muted font-mono">
                          {project.slug}
                        </span>
                      </button>
                    ))
                  )}
                </div>
              ))
            )}
          </div>
        </>
      )}
    </div>
  );
}
